import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import get from "lodash/get";
import forEach from "lodash/forEach";
import includes from "lodash/includes";
import map from "lodash/map";
import sum from "lodash/sum";
import keys from "lodash/keys";
import find from "lodash/find";
import head from "lodash/head";
import size from "lodash/size";

import { getContact } from "../../helpers/utils";
import {
  digitalAdvertisingOptions1,
  digitalAdvertisingOptions2,
  digitalAdvertisingOptions3,
  SIGNATURES,
} from "../../helpers/constants";
import { pick } from "lodash";

const useSyncFormData = ({
  setValue,
  getValues,
  ownerId,
  startDate,
  data,
  previewDocument,
  watch,
  relatedDocuments,
  query,
}) => {
  const initDraftRef = useRef(false);

  const {
    estate,
    units,
    standaloneEstates,
    history,
    advertisements,
    customersFollowUp,
    analytics,
  } = pick(data, [
    "estate",
    "units",
    "standaloneEstates",
    "history",
    "advertisements",
    "customersFollowUp",
    "analytics",
  ]);

  console.log("data", data);

  const setProjectInfo = () => {
    setValue("project.date", dayjs(startDate).locale("fr").format("MMMM YYYY"));
    setValue("project.name", get(estate, "name"));
    setValue(
      "project.address",
      [get(estate, "address"), get(estate, "number")].filter(Boolean).join(" ")
    );
    setValue(
      "project.country",
      [get(estate, "zip"), get(estate, "city")].filter(Boolean).join(" ")
    );
  };

  const setOwnerInfo = () => {
    const owner = getContact(estate?.contacts || [], ownerId);
    setValue("project.owner.firstName", get(owner, "firstName"));
    setValue("project.owner.name", get(owner, "name"));
    setValue("project.owner.email", get(owner, "privateEmail"));
    setValue("project.owner.mobile", get(owner, "privateMobile"));
  };

  const setLeadReceived = () => {
    setValue(
      "history.leadReceived.website",
      get(history, ["leadReceived", "website"])
    );
    setValue(
      "history.leadReceived.whise",
      get(history, ["leadReceived", "whise"])
    );
  };

  const setSignedCompromises = () =>
    setValue("history.signedCompromises", get(history, "signedCompromises"));

  const setDigitalAdvertising = () => {
    const values = get(advertisements, "digitalAdvertising", []);

    if (values.length > 0) {
      const initValues = getValues("advertise.digitalAdvertising") || [];
      setValue("advertise.digitalAdvertising", [
        ...new Set([...initValues, ...values]),
      ]);
    }
  };

  const setMailingsProject = () =>
    setValue(
      "advertise.mailingsProject",
      get(advertisements, ["mailingsProject"], 0)
    );

  const setMailingsUnits = () =>
    setValue(
      "advertise.mailingsUnits",
      get(advertisements, ["mailingsUnits"], 0)
    );

  const setMailingsAutomatic = () =>
    setValue(
      "advertise.mailingsAutomatic",
      get(advertisements, ["mailingsAutomatic"], 0)
    );

  const getCustomersFollowUp = () => {
    setValue("customersFollowUp", customersFollowUp);
  };

  const setTotalLead = () => {
    const promoter = parseInt(getValues("history.leadReceived.promoter"));
    const whise = parseInt(getValues("history.leadReceived.whise"));
    const website = parseInt(getValues("history.leadReceived.website"));
    setValue("history.leadReceived.total", promoter + whise + website);
  };

  const setAnalytics = () => {
    const estateIds = keys(analytics);
    const aggregateValue = {};
    for (const id of estateIds) {
      const estateHistories = get(analytics, id);
      const dates = keys(estateHistories);
      for (const date of dates) {
        if (!aggregateValue[date]) {
          aggregateValue[date] = 0;
        }
        aggregateValue[date] += get(estateHistories, date, 0);
      }
    }

    let orderedDates = keys(aggregateValue).sort();
    const orderedData = orderedDates.map((date) =>
      get(aggregateValue, date, 0)
    );
    orderedDates = map(orderedDates, (date) => date.substring(6));

    const totalViews = sum(orderedData);
    setValue("history.websiteViewLabel", JSON.stringify(orderedDates));
    setValue("history.websiteViewData", JSON.stringify(orderedData));
    setValue("history.websiteView", totalViews);
  };

  const setPhoneCalls = () =>
    setValue("history.calls", get(history, "calls", 0));

  const setVisits = () => {
    setValue("history.visits.office", get(history, ["visits", "office"], 0));
    setValue(
      "history.visits.worksite",
      get(history, ["visits", "worksite"], 0)
    );
  };

  const syncForm = () => {
    const draftId = query?.get("draft");
    if (!!draftId && !initDraftRef.current) {
      initDraftRef.current = true;
      return;
    }
    setProjectInfo();
    setOwnerInfo();
    setLeadReceived();
    setSignedCompromises();
    setDigitalAdvertising();
    setMailingsProject();
    setMailingsUnits();
    setMailingsAutomatic();
    getCustomersFollowUp();
    setTotalLead();
    setAnalytics();
    setPhoneCalls();
    setVisits();
  };

  useEffect(() => {
    if (!!estate) {
      syncForm();
      !!previewDocument && previewDocument();
    }
  }, [estate, data]);

  useEffect(() => {
    setTotalLead();
  }, [
    watch("history.leadReceived.promoter"),
    watch("history.leadReceived.whise"),
    watch("history.leadReceived.website"),
  ]);

  useEffect(() => {
    const hasPanel = getValues("advertise.panel");
    setValue("advertise.panelLabel", hasPanel == "true" ? "oui" : "non");
  }, [watch("advertise.panel")]);

  useEffect(() => {
    const officeId = get(estate, "officeId");
    const signature =
      find(SIGNATURES, ({ offices }) => includes(offices, officeId)) ||
      head(SIGNATURES);
    setValue("signatures", get(signature, "values", []));
  }, [estate]);

  useEffect(() => {
    const sponsorLogo = getValues("sponsorLogo");
    const hasLogo = !!sponsorLogo && sponsorLogo.length > 0;
    const isBase64 = !!sponsorLogo && typeof sponsorLogo === "string";
    if (hasLogo || isBase64) {
      const sponsorLogoPreview = getValues("sponsorLogoPreview");
      if (sponsorLogoPreview) {
        URL.revokeObjectURL(sponsorLogoPreview);
      }

      if (isBase64) {
        setValue("sponsorLogoPreview", sponsorLogo);
      } else {
        const objectURL = URL.createObjectURL(head(sponsorLogo));
        setValue("sponsorLogoPreview", objectURL);
      }
      !!previewDocument && previewDocument();
    }
  }, [watch("sponsorLogo")]);

  useEffect(() => {
    if (size(relatedDocuments)) {
      const selectedAds = getValues("advertise.digitalAdvertising");

      const allDigitalAdvertising = [
        ...digitalAdvertisingOptions1,
        ...digitalAdvertisingOptions2,
        ...digitalAdvertisingOptions3,
      ];

      forEach(relatedDocuments, (relDoc) => {
        const docType = relDoc.documentTypeId;
        const ad = allDigitalAdvertising.find((ad) => ad.id === docType);
        if (!!ad) {
          const adName = ad.name.toLowerCase();
          if (!selectedAds.includes(adName)) {
            selectedAds.push(adName);
          }
        }
      });
      !!previewDocument && previewDocument();
    }
  }, [relatedDocuments]);
};

export default useSyncFormData;
